import React from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { CheckCircle2, Package, ArrowRight, MessageCircle } from "lucide-react";
import { inr, WHATSAPP } from "../lib/api";

export default function OrderConfirmation() {
  const { state } = useLocation();
  const order = state?.order;
  if (!order) return <Navigate to="/" replace />;

  return (
    <div className="max-w-2xl mx-auto px-5 py-16 text-center" data-testid="order-confirmation">
      <CheckCircle2 size={56} className="mx-auto text-primary" strokeWidth={1.3} />
      <h1 className="mt-6 text-4xl text-primary font-medium">Thank you!</h1>
      <p className="mt-3 text-muted-foreground">
        Your order has been placed. A confirmation will follow on WhatsApp / email.
      </p>

      <div className="mt-8 border border-border text-left">
        <div className="bg-secondary/50 px-6 py-4 flex items-center justify-between border-b border-border">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Order ID</p>
            <p className="font-display text-lg text-primary" data-testid="confirm-order-number">{order.order_number}</p>
          </div>
          <Package size={28} className="text-primary/60" />
        </div>
        <div className="px-6 py-5 space-y-3">
          {order.items.map((i, idx) => (
            <div key={idx} className="flex justify-between text-sm">
              <span className="text-foreground/80">{i.name} × {i.qty}</span>
              <span className="font-semibold">{inr(i.price * i.qty)}</span>
            </div>
          ))}
          <div className="border-t border-border pt-3 space-y-2">
            {order.discount > 0 && (
              <div className="flex justify-between text-sm text-primary" data-testid="confirm-discount">
                <span>Coupon ({order.coupon_code})</span>
                <span className="font-semibold">− {inr(order.discount)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="font-semibold">Total</span>
              <span className="font-semibold text-primary">{inr(order.total)}</span>
            </div>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Payment</span>
            <span className="capitalize">
              {order.payment_method === "cod" ? "Cash on Delivery" : "Paid online"}
            </span>
          </div>
        </div>
      </div>

      <p className="mt-6 text-sm text-muted-foreground">
        Track your order anytime using your Order ID and email/phone.
      </p>
      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/track"
          state={{ order_number: order.order_number }}
          className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 text-sm"
          data-testid="confirm-track-btn"
        >
          Track Order <ArrowRight size={16} />
        </Link>
        <Link to="/shop" className="inline-flex items-center justify-center gap-2 border border-primary/30 px-8 py-3.5 text-sm hover:bg-secondary transition-colors">
          Continue Shopping
        </Link>
      </div>
      <a
        href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(`Hi, my order ${order.order_number}`)}`}
        target="_blank"
        rel="noreferrer"
        className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
      >
        <MessageCircle size={16} /> Questions? Chat on WhatsApp
      </a>
    </div>
  );
}
